import {
  receivedInvoicesPaymentStatusesMap as italyReceivedInvoicesPaymentStatusesMap,
  receivedInvoicesStatusesMap as italyReceivedInvoicesStatusesMap,
} from '@/src/app/core/country-configs/italy/received-invoices-data';
import { IStatusChip } from '@/src/app/shared/components/status-chip/status-chip.interface';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { SearchType } from 'filter-data';
import clone from 'just-clone';
import { EReceivedInvoiceStatus, TReceivedInvoiceStatuses } from '../../../enums';
import { IApiFiltersList } from '../../../interfaces/filters.interface';
import { EApiCallStateKey, EApiRequestPartKeys, EApiRequestPartsNames } from '../api-call-state.enum';
import { IApiCallState } from '../api-call-state.interface';

import { docCollFilterDefinition } from '@/src/app/modules/invoices/data/attached-document-type.filter';
import { fileTypeFilterDefinition } from '@/src/app/modules/invoices/data/file-type.filter';

const createStatusOptions = (statusesMap: Partial<Record<EReceivedInvoiceStatus, IStatusChip>>) =>
  Object.entries(statusesMap)
    .filter(([, statusChip]) => !!statusChip)
    .map(([value, statusChip]) => ({
      value,
      label: statusChip!.label,
    }));

const createPaymentStatusOptions = (paymentStatusesMap: Partial<Record<string, IStatusChip>>) =>
  Object.entries(paymentStatusesMap)
    .filter(([, statusChip]) => !!statusChip)
    .map(([value, statusChip]) => ({
      value,
      label: statusChip!.label,
    }));

export const createReceivedInvoicesApiCallState = (
  statusesMap: Partial<Record<EReceivedInvoiceStatus, IStatusChip>> = italyReceivedInvoicesStatusesMap,
  receivedInvoiceStatuses: TReceivedInvoiceStatuses = EReceivedInvoiceStatus,
  paymentStatusesMap: Partial<Record<string, IStatusChip>> = italyReceivedInvoicesPaymentStatusesMap
): IApiCallState => {
  const statusOptions = createStatusOptions(statusesMap);
  const allStatuses = statusOptions.map(option => option.value) as EReceivedInvoiceStatus[];
  const paymentStatusOptions = createPaymentStatusOptions(paymentStatusesMap);
  const allPaymentStatuses = paymentStatusOptions.map(option => option.value);

  return {
    key: EApiCallStateKey.RECEIVED_INVOICES,
    filtersUpdated: false,
    requestParts: [
      {
        key: EApiRequestPartKeys.PAGINATION,
        partName: EApiRequestPartsNames.PAGINATION,
        data: {
          page: 0,
          size: 10,
          totalItems: 0,
        },
      },
      {
        key: EApiRequestPartKeys.SORTING,
        partName: EApiRequestPartsNames.SORTING,
        data: {},
      },
      {
        key: EApiRequestPartKeys.FILTERS,
        partName: EApiRequestPartsNames.FILTER,
        data: {
          workedUsers: {
            id: 'workedUsers',
            property: 'workedUsers',
            value: null,
            defaultValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
          },
          fiscalYear: {
            id: 'fiscalYear',
            property: 'fiscalYear',
            value: null,
            defaultValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
          },
          date: {
            id: 'date',
            property: 'date',
            value: null,
            defaultValue: null,
            complexValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'date',
                minDate: new Date('2000-01-01'),
                maxDate: new Date(),
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
            complexValueTemplate: {
              type: 'UgualeA',
              value: null,
            },
          },
          receivingDate: {
            id: 'receivingDate',
            property: 'receivingDate',
            value: null,
            defaultValue: null,
            complexValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'date',
                minDate: new Date('2000-01-01'),
                maxDate: new Date(),
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
            complexValueTemplate: {
              type: 'UgualeA',
              value: null,
            },
          },
          number: {
            id: 'number',
            property: 'number',
            value: null,
            defaultValue: null,
            complexValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
                maxLength: 100,
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
            complexValueTemplate: {
              type: 'Contiene',
              value: '',
            },
          },
          sender: {
            id: 'sender',
            property: 'sender',
            value: null,
            complexValue: null,
            defaultValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
                maxLength: 100,
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
            complexValueTemplate: {
              type: 'Contiene',
              value: '',
            },
          },
          sdiFileName: {
            id: 'sdiFileName',
            property: 'sdiFileName',
            value: null,
            complexValue: null,
            defaultValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
                maxLength: 100,
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
            complexValueTemplate: {
              type: 'Contiene',
              value: '',
            },
          },
          docColl: clone(docCollFilterDefinition),
          keyword: {
            id: 'keyword',
            property: 'keyword',
            value: null,
            defaultValue: null,
            comparator: SearchType.EQ,
            ui: {
              default: {
                type: 'input',
              },
            },
            chips: {
              label: marker(''),
              show: false,
              type: 'value',
            },
          },
          fileType: clone(fileTypeFilterDefinition),
          status: {
            id: 'status',
            property: 'status',
            doNotSendWhenDefault: true,
            value: allStatuses,
            defaultValue: allStatuses,
            defaultValues: [allStatuses],
            comparator: SearchType.EQ,
            ui: {
              buttons: {
                type: 'buttons-multiple',
                options: [
                  {
                    value: allStatuses,
                    label: marker('RECEIVED_INVOICES.FILTERS.STATUS.SHOW_ALL'),
                  },
                  {
                    value: [receivedInvoiceStatuses.NOT_READ],
                    label: marker('RECEIVED_INVOICES.FILTERS.STATUS.SHOW_NOT_READ'),
                  },
                ],
              },
              multiple: {
                type: 'multiple',
                options: statusOptions,
              },
            },
            chips: {
              label: '',
              show: false,
              type: 'options',
              options: statusOptions,
            },
          },
          paymentStatus: {
            id: 'paymentStatus',
            property: 'collectionPaymentStatus',
            doNotSendWhenDefault: true,
            value: allPaymentStatuses,
            defaultValue: allPaymentStatuses,
            defaultValues: [allPaymentStatuses],
            comparator: SearchType.EQ,
            ui: {
              multiple: {
                type: 'multiple',
                options: paymentStatusOptions,
              },
            },
            chips: {
              label: '',
              show: false,
              type: 'options',
              options: paymentStatusOptions,
            },
          },
        } as IApiFiltersList,
      },
    ],
  };
};
